/**
 * SaveBar — Agency Portal v2 Slice 4 (§4.0).
 *
 * One obvious "Save changes" button per form. States:
 *
 *   clean   | mono "No changes"           (muted)
 *   dirty   | amber dot + "Unsaved changes"
 *   saving  | "Saving…"
 *   saved   | lime check + "Saved · 14:02" (sticks until next edit)
 *
 * "Save changes" is disabled unless the form is dirty and valid.
 * "Discard" shows only when dirty.
 *
 * The caller owns the dirty/valid/saved-at computation; this atom is
 * pure presentation + wiring to Button atoms.
 */

import { Check } from 'lucide-react';

import { useC } from '../../lib/theme.js';
import { Button } from './Button.js';

const monoFont = "'JetBrains Mono', monospace";

export type SaveBarStatus = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

export interface SaveBarProps {
  readonly status: SaveBarStatus;
  readonly savedAt?: Date | null;
  readonly canSave: boolean;
  readonly onSave: () => void;
  readonly onDiscard?: () => void;
  readonly saveLabel?: string;
  readonly 'data-testid'?: string;
}

function formatHm(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function SaveBar({
  status,
  savedAt,
  canSave,
  onSave,
  onDiscard,
  saveLabel = 'Save changes',
  ...rest
}: SaveBarProps): JSX.Element {
  const C = useC();
  const showDiscard = (status === 'dirty' || status === 'error') && onDiscard !== undefined;

  let statusEl: JSX.Element;
  switch (status) {
    case 'clean':
      statusEl = <span style={{ color: C.muted }}>No changes</span>;
      break;
    case 'dirty':
      statusEl = (
        <span style={{ color: C.amberFg, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: 999,
              background: C.amber,
            }}
          />
          Unsaved changes
        </span>
      );
      break;
    case 'saving':
      statusEl = <span style={{ color: C.muted }}>Saving…</span>;
      break;
    case 'saved':
      statusEl = (
        <span style={{ color: C.limeFg, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Check size={12} color={C.limeFg} />
          Saved{savedAt ? ` · ${formatHm(savedAt)}` : ''}
        </span>
      );
      break;
    case 'error':
      statusEl = <span style={{ color: C.coralFg }}>Not saved</span>;
      break;
  }

  return (
    <div
      data-testid={rest['data-testid'] ?? 'save-bar'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        paddingTop: 12,
        borderTop: `1px solid ${C.borderSoft}`,
      }}
    >
      <div
        aria-live="polite"
        role="status"
        style={{
          flex: '1 1 auto',
          fontFamily: monoFont,
          fontSize: 11,
          letterSpacing: 0.5,
        }}
      >
        {statusEl}
      </div>
      {showDiscard ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDiscard}
          data-testid="save-bar-discard"
        >
          Discard
        </Button>
      ) : null}
      <Button
        variant="primary"
        size="md"
        loading={status === 'saving'}
        disabled={!canSave}
        onClick={onSave}
        data-testid="save-bar-save"
      >
        {saveLabel}
      </Button>
    </div>
  );
}
